import React, { useState, useEffect, useContext } from "react";
import api from "../../../services/AxiosConnection";
import AuthContext from "../../../context/AuthContext";

function MessageQueStatus() {
  // State to hold table data and selected time period
  const [tableData, setTableData] = useState([]);
  const [selectedOption, setSelectedOption] = useState("Last 7 Days");
  const [loading, setLoading] = useState(false);
  const { selectedSite } = useContext(AuthContext);

  // Function to fetch SMS Queue data from API
  const fetchSMSQueueData = async (selectedOption) => {
    setLoading(true); // Set loading to true before fetching data
    try {
      const response = await api.get(
        `/control-panel/getSMSQueueByDate?siteDb=${selectedSite}&selectedOption=${selectedOption}`
      );
      
      // Setting table data from the API response
      if (response.data && response.data.data) {
        setTableData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching SMS queue data:", error);
      setTableData([]);
    } finally {
      setLoading(false); // Set loading to false once the data has been fetched
    }
  };

  // Fetch data on component load and whenever the selectedOption changes
  useEffect(() => {
    fetchSMSQueueData(selectedOption);
  }, [selectedOption,selectedSite]);

  // Function to get CSS class for status
  const getStatusClass = (status) => {
    switch (status) {
      case "Sent":
        return "text-green-600";
      case "Pending":
        return "text-orange-600";
      case "Failed":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  // Function to format date as DD-MM-YYYY
  const formatDate = (dateString) => {
    const date = new Date(dateString);  // Parse the date string
    // Convert to UTC (no timezone adjustment)
    const utcDate = new Date(date.toLocaleString("en-US", { timeZone: "UTC" }));
    return utcDate.toLocaleDateString("en-GB"); // Format as DD-MM-YYYY
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-blue-50 p-6 rounded-lg mb-6">
        <h2 className="text-lg font-bold text-blue-800 mb-2">Message Queue Status</h2>
        <p className="text-gray-600 text-xs">
          Check the status of messages sent from the system. The table below shows details of the alerts and their delivery status.
        </p>
      </div>

      {/* Dropdown for selecting the time period */}
      <div className="mb-4">
        <label htmlFor="timePeriod" className="mr-2 text-sm font-medium text-gray-700">Select Time Period:</label>
        <select
          id="timePeriod"
          value={selectedOption}
          onChange={(e) => setSelectedOption(e.target.value)}
          className="border border-gray-300 text-sm rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ease-in-out shadow-sm hover:shadow-md"
        >
          <option value="Last 7 Days">Last 7 Days</option>
          <option value="Last 15 Days">Last 15 Days</option>
          <option value="Last 1 Month">Last 1 Month</option>
          <option value="Last 3 Months">Last 3 Months</option>
          <option value="Last 6 Months">Last 6 Months</option>
        </select>
      </div>

      {/* Loading state */}
      {loading ? (
        <div className="text-center text-lg text-gray-600">Loading data...</div> // Show loading message while data is being fetched
      ) : tableData.length === 0 ? ( // Check if tableData is empty
        <div className="text-center text-lg text-gray-600">No message status available</div> // Show message when no data is available
      ) : (
        <div className="overflow-x-auto">
          {/* Table Section */}
          <table className="table-auto w-full border-collapse border border-gray-300 text-xs">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-2 text-left">Train ID</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Phone Number</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Message</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Date of Creation</th>
                <th className="border border-gray-300 px-4 py-2 text-left">SMS Delivery Status</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, index) => (
                <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  <td className="border border-gray-300 px-4 py-2">{row.trainId || "N/A"}</td>
                  <td className="border border-gray-300 px-4 py-2">{row.PhoneNumber}</td>
                  <td className="border border-gray-300 px-4 py-2">{row.Message}</td>
                  <td className="border border-gray-300 px-4 py-2">
                    {formatDate(row.DateofCreation)}
                  </td>
                  <td className={`border border-gray-300 px-4 py-2 font-semibold ${getStatusClass(row.Status)}`}>
                    {row.Status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default MessageQueStatus;
